import { faArrowUpFromBracket } from "@fortawesome/pro-solid-svg-icons";
import {
  Button,
  FileInputArea,
  Icon,
  Input,
  Message,
} from "@intility/bifrost-react";
import { useMemo, useState } from "react";
import type { PlatformLogoRequest } from "../../api/types";
import {
  LOGO_ACCEPT,
  MAX_LOGO_DATA_LENGTH,
  readLogoFile,
} from "../../lib/platformIdentity";
import { useLogoRegistry } from "./useLogoRegistry";
import "./PlatformLogoPicker.css";

// The logo part of a platform's identity, as an editable control. A logo is
// one of: none, a builtin logo picked from the @intility/logos registry, or a
// custom uploaded image (base64 data URI). The parent owns the selection; the
// theme that follows a builtin logo is decided there, not here.
export type LogoSelection =
  | { type: "none" }
  | { type: "builtin"; key: string }
  | { type: "custom"; data: string };

export function logoSelectionToRequest(
  selection: LogoSelection,
): PlatformLogoRequest | undefined {
  switch (selection.type) {
    case "none":
      return undefined;
    case "builtin":
      return { type: "builtin", key: selection.key };
    case "custom":
      return { type: "custom", data: selection.data };
  }
}

export function PlatformLogoPicker({
  value,
  onChange,
}: {
  value: LogoSelection;
  onChange: (selection: LogoSelection) => void;
}) {
  const registry = useLogoRegistry();
  const [query, setQuery] = useState("");
  const [uploadError, setUploadError] = useState<string | null>(null);

  const entries = useMemo(
    () =>
      Object.entries(registry ?? {})
        .filter(([id]) => id.includes(query.trim().toLowerCase()))
        .sort(([a], [b]) => a.localeCompare(b)),
    [registry, query],
  );

  async function handleFile(file: File | undefined) {
    if (!file) return;
    setUploadError(null);
    try {
      const data = await readLogoFile(file);
      onChange({ type: "custom", data });
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : String(err));
    }
  }

  return (
    <div className="platform-logo-picker">
      <Input
        label="Pick a service logo"
        hideLabel
        placeholder="Search service logos…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />

      {!registry ? (
        <div
          className="platform-logo-grid"
          style={{ minHeight: 120, placeContent: "center" }}
        >
          <Icon.Spinner size={24} />
        </div>
      ) : (
        <div
          className="platform-logo-grid"
          role="listbox"
          aria-label="Service logos"
        >
          {entries.map(([id, dataUri]) => (
            <button
              key={id}
              type="button"
              className="platform-logo-option"
              role="option"
              aria-selected={value.type === "builtin" && value.key === id}
              onClick={() => onChange({ type: "builtin", key: id })}
              title={id}
            >
              <img src={dataUri} alt="" />
              <span className="platform-logo-option-label">{id}</span>
            </button>
          ))}
          {entries.length === 0 && (
            <p className="bf-p bfc-base-2" style={{ margin: 0 }}>
              No logo matches "{query}".
            </p>
          )}
        </div>
      )}

      <div className="platform-logo-upload">
        <FileInputArea
          aria-label="Upload a custom logo"
          accept={LOGO_ACCEPT}
          onInput={(e) => {
            handleFile(e.currentTarget.files?.[0]);
            e.currentTarget.value = "";
          }}
        >
          <Icon
            icon={faArrowUpFromBracket}
            className="bfc-base-2"
            style={{ marginBottom: 8 }}
          />
          <div>
            Upload your own logo or{" "}
            <span className="bf-neutral-link-text">click to choose a file</span>
          </div>
          <div className="bfc-base-2">
            PNG, JPG or WebP, under about 256 KiB
          </div>
        </FileInputArea>
        {uploadError && (
          <Message state="alert" header="Could not use that logo">
            {uploadError}
          </Message>
        )}
      </div>

      <div className="platform-logo-current">
        {value.type === "none" ? (
          <span className="bf-p bfc-base-2" style={{ margin: 0 }}>
            No logo selected. The platform shows a placeholder.
          </span>
        ) : value.type === "builtin" ? (
          <span className="bf-p" style={{ margin: 0 }}>
            Service logo: <code>{value.key}</code>
          </span>
        ) : (
          <span className="bf-p" style={{ margin: 0 }}>
            Custom logo (
            {value.data.length > MAX_LOGO_DATA_LENGTH
              ? "too large"
              : `${Math.max(1, Math.round(value.data.length / 1398))} KiB`}
            )
          </span>
        )}
        {value.type !== "none" && (
          <Button
            small
            variant="flat"
            onClick={() => onChange({ type: "none" })}
          >
            Remove logo
          </Button>
        )}
      </div>
    </div>
  );
}
